import { useMemo } from 'react';
import { usePracticeDataset } from '../components/onboarding/practice/practiceDataset';
import { enrichCostPriceListWithCurrentPrices } from '../utils/costPriceUtils';
import { EnrichedCostPriceAnalysis } from '../services/costPrice/costPriceService';
import { useByRestaurantCostData } from './useByRestaurantCostData';
import { useCostPriceStore } from '../stores/appCostPriceStore';
import { useCostPriceCategoriesStore } from '../stores/appCostPriceCategoriesStore';
import { useOrderHistory } from '../stores/appOrderHistoryStore';
import { useRevenueHistory } from '../stores/appRevenueHistoryStore';
import { useSubrecipesStore } from '../stores/appSubrecipesStore';
import { useAppUserDefaultStore } from '../stores/appUserDefaultStore';
import { transformCostPriceListToDishData } from '../components/pages/costPrice/new/dataAdapter';
import { DishRowData } from '../components/pages/costPrice/new/costPriceDataTypes';
import { applyMenuEngineeringMatrix } from '../services/costPrice/menuEngineeringService';
import { CostTargets } from '../utils/costTargets';

interface UseCostPriceDishDataParams {
  restaurantId?: string;
  practice?: boolean;
  // Revenue item names that should also surface as placeholder rows (dishes with
  // no analysis yet). Pass [] when only existing analyses matter.
  revenueItems: string[];
  costTargets: CostTargets;
  defaultWastePercentage: number;
}

/**
 * The cost-price row pipeline shared by every page that needs live dish rows:
 * cross-restaurant sourcing → price enrichment → DishRowData transform → menu
 * engineering matrix. Extracted from the cost price page so the automation
 * review page renders the exact same live numbers without duplicating the chain.
 */
export function useCostPriceDishData({
  restaurantId,
  practice = false,
  revenueItems,
  costTargets,
  defaultWastePercentage,
}: UseCostPriceDishDataParams): { enrichedCostPriceList: EnrichedCostPriceAnalysis[]; dishData: DishRowData[] } {
  const practiceData = usePracticeDataset(practice);
  const liveCostPriceList = useCostPriceStore(s => s.costPriceList);
  const costPriceList = practiceData?.costPriceList ?? liveCostPriceList;
  const liveCustomIngredients = useCostPriceStore(s => s.customIngredients);
  const customIngredients = practiceData?.customIngredients ?? liveCustomIngredients;
  const liveCategories = useCostPriceCategoriesStore(s => s.categories);
  const categories = practiceData?.categories ?? liveCategories;
  const liveOrderHistoryList = useOrderHistory(s => s.orderHistoryList);
  const orderHistoryList = practiceData?.orderHistoryList ?? liveOrderHistoryList;
  const liveRevenueHistoryList = useRevenueHistory(s => s.revenueHistoryList);
  const revenueHistoryList = practiceData?.revenueHistoryList ?? liveRevenueHistoryList;
  const liveSubrecipesById = useSubrecipesStore(s => s.subrecipesById);
  const subrecipesById = practiceData?.subrecipesById ?? liveSubrecipesById;
  const showExVatOrders = useAppUserDefaultStore(s => s.showExVatOrders);

  if (practiceData) {
    revenueItems = practiceData.revenueItems;
    defaultWastePercentage = 0;
  }

  // Cross-restaurant sourcing: load the data sets of exactly the sibling
  // restaurants referenced by the loaded analyses (no extra fetch when nothing
  // is cross-restaurant), keyed for enrichment so each borrowed ingredient is
  // priced live from its source restaurant's data.
  const byRestaurant = useByRestaurantCostData(costPriceList, restaurantId);

  // Enrich cost price data with current prices from order history + subrecipes store.
  // Pass revenueHistory so BYO dishes get their weighted breakdown attached (B3);
  // non-BYO analyses are completely unaffected (`byo` stays undefined).
  const enrichedCostPriceList = useMemo(() =>
    enrichCostPriceListWithCurrentPrices(
      costPriceList,
      orderHistoryList,
      {
        customIngredients,
        showExVat: showExVatOrders,
        allSubrecipes: subrecipesById,
        revenueHistory: revenueHistoryList,
        byRestaurant,
        currentRestaurantId: restaurantId,
        defaultWastePercentage,
      }
    ),
    [costPriceList, orderHistoryList, customIngredients, showExVatOrders, subrecipesById, revenueHistoryList, byRestaurant, restaurantId, defaultWastePercentage]
  );

  // Transform data to DishRowData format for the table and apply menu engineering matrix
  const dishData = useMemo(() => {
    const transformedData = transformCostPriceListToDishData(
      enrichedCostPriceList,
      revenueHistoryList,
      revenueItems,
      categories,
      showExVatOrders,
      restaurantId
    );

    // Apply menu engineering matrix classification. Final ordering is
    // driven by the column sort in the table (default: cost % asc).
    return applyMenuEngineeringMatrix(transformedData, costTargets);
  }, [enrichedCostPriceList, revenueHistoryList, revenueItems, categories, showExVatOrders, restaurantId, costTargets]);

  return { enrichedCostPriceList, dishData };
}
